/**
 * ModalEvento - Formulario de edición/creación de eventos
 * 
 * Props:
 * - open: boolean que indica si el modal está abierto
 * - evento: objeto con datos del evento (null para crear nuevo)
 * - grupos: array de grupos disponibles
 * - saving: boolean que indica si se está guardando
 * - onCancel: función para cancelar
 * - onSave: función para guardar (recibe el objeto actualizado)
 */

import React, { useState, useEffect } from "react";
import Modal from "antd/es/modal/Modal";
import { listCollaborators } from "../services/collaboratorsService";

// === Tipos de evento ===
const TIPO_EVENTO = {
  ENTRENAMIENTO: "entrenamiento",
  TORNEO: "torneo",
};

const ESTADO_EVENTO = {
  PROGRAMADO: "programado",
  EN_CURSO: "en_curso",
  FINALIZADO: "finalizado",
  CANCELADO: "cancelado",
};

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  disabled = false,
  options = [],
  inputProps = {},
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {type === "select" ? (
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
          required={required}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          required={required}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
          required={required}
          {...inputProps}
        />
      )}
    </label>
  );
}




export function ModalEvento({
  open,
  evento,
  grupos = [],
  saving,
  onCancel,
  onSave,
}) {
  const isEditing = !!evento?.id;
  const [loading, setLoading] = useState(true);
  const [entrenadores, setEntrenadores] = useState([]);
  const [formData, setFormData] = useState({
    id: "",
    nombre: "",
    tipo: TIPO_EVENTO.ENTRENAMIENTO,
    descripcion: "",
    fecha_inicio: "",
    fecha_fin: "",
    ubicacion: "",
    estado: ESTADO_EVENTO.PROGRAMADO,
    grupo_id: "",
    colaborador_id: "",
  });

  // Cargar datos del evento al montar el componente
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Simular pequeña carga para consistencia
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (evento) {
          setFormData({
            id: evento.id || "",
            nombre: evento.nombre || "",
            tipo: evento.tipo || TIPO_EVENTO.ENTRENAMIENTO,
            descripcion: evento.observaciones || "",
            fecha_inicio: evento.fecha_inicio
              ? evento.fecha_inicio.slice(0, 16)
              : "",
            fecha_fin: evento.fecha_fin ? evento.fecha_fin.slice(0, 16) : "",
            ubicacion: evento.ubicacion || "",
            estado: evento.estado || ESTADO_EVENTO.PROGRAMADO,
            grupo_id: evento.grupo_id || "",
            colaborador_id: evento.colaborador_id || "",
          });
        } else {
          // Limpiar formulario cuando se abre para crear
          setFormData({
            id: "",
            nombre: "",
            tipo: TIPO_EVENTO.ENTRENAMIENTO,
            descripcion: "",
            fecha_inicio: "",
            fecha_fin: "",
            ubicacion: "",
            estado: ESTADO_EVENTO.PROGRAMADO,
            grupo_id: "",
            colaborador_id: "",
          });
        }
      } catch (error) {
        console.error("Error cargando datos del evento:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadData();
    }
  }, [evento, open]);

  // Cuando cambia el tipo, limpiar grupo_id si es torneo
  const handleTipoChange = (nuevoTipo) => {
    setFormData({
      ...formData,
      tipo: nuevoTipo,
      grupo_id: nuevoTipo === TIPO_EVENTO.TORNEO ? "" : formData.grupo_id,
    });
  };

  // Cargar entrenadores (colaboradores) cuando se abre el modal
  useEffect(() => {
    let mounted = true;
    const loadEntrenadores = async () => {
      try {
        const colabs = await listCollaborators?.();
        if (!mounted) return;

        // Filtro tolerante para “entrenador” (igual que en Grupos.js)
        const COACH_ROLE_ID = "1b6632eb-5967-4493-ae24-8ed9e29d590d";
        const onlyCoaches = (colabs || []).filter((c) => {
          const roleId = c.rol_id || c.role_id || c.rol?.id || c.role?.id;
          const roleName = (
            (c.rol_nombre || c.role_name || c.rol?.nombre || c.role?.name || "")
          )
            .toString()
            .toLowerCase();
          return (
            roleId === COACH_ROLE_ID ||
            roleName.includes("entrenador") ||
            roleName.includes("coach")
          );
        });

        setEntrenadores(onlyCoaches);
      } catch (err) {
        console.error("Error cargando entrenadores:", err);
        setEntrenadores([]);
      }
    };

    if (open) {
      loadEntrenadores();
    }
    return () => {
      mounted = false;
    };
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();

      // Validación de fechas
  const now = new Date().toISOString().slice(0, 16);
  
  if (formData.fecha_inicio < now) {
    alert("La fecha y hora de inicio no puede ser anterior a la fecha y hora actual.");
    return;
  }
  
  if (formData.fecha_fin < now) {
    alert("La fecha y hora de fin no puede ser anterior a la fecha y hora actual.");
    return;
  }
  
  if (formData.fecha_fin < formData.fecha_inicio) {
    alert("La fecha y hora de fin no puede ser anterior a la fecha y hora de inicio.");
    return;
  }
    // Si es torneo, enviar grupo_id como null
    const dataToSave = {
      ...formData,
      grupo_id:
        formData.tipo === TIPO_EVENTO.TORNEO ? null : formData.grupo_id,
      // Enviar colaborador_id sólo cuando sea torneo, sino null
      colaborador_id:
        formData.tipo === TIPO_EVENTO.TORNEO
          ? formData.colaborador_id || null
          : null,
    };
    onSave(dataToSave);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={isEditing ? "Editar evento" : "Nuevo evento"}
      footer={null}
      width={700}
    >
      {loading ? (
        <div className="space-y-3">
          <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                <div className="h-9 w-full bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>
          <div className="space-y-1">
            <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Tipo de evento - destacado al inicio */}
          <div className="rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white p-4">
            <Field
              label="Tipo de evento"
              value={formData.tipo}
              onChange={handleTipoChange}
              type="select"
              disabled={saving}
              options={[
                {
                  value: TIPO_EVENTO.ENTRENAMIENTO,
                  label: "🏃 Entrenamiento",
                },
                { value: TIPO_EVENTO.TORNEO, label: "🏆 Torneo" },
              ]}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Nombre del evento"
              value={formData.nombre}
              onChange={(v) => setFormData({ ...formData, nombre: v })}
              disabled={saving}
              placeholder="Ej: Entrenamiento técnico sub-15"
              required
            />
            <Field
              label="Ubicación"
              value={formData.ubicacion}
              onChange={(v) => setFormData({ ...formData, ubicacion: v })}
              disabled={saving}
              placeholder="Ej: Cancha Principal"
              required
            />
           <Field
  label="Fecha y hora de inicio"
  type="datetime-local"
  value={formData.fecha_inicio}
  onChange={(v) => {
    setFormData({ ...formData, fecha_inicio: v });
  }}
  disabled={saving}
  required
  inputProps={{
    min: new Date().toISOString().slice(0, 16),
  }}
/>
<Field
  label="Fecha y hora de fin"
  type="datetime-local"
  value={formData.fecha_fin}
  onChange={(v) => {
    setFormData({ ...formData, fecha_fin: v });
  }}
  disabled={saving}
  required
  inputProps={{
    min: formData.fecha_inicio || new Date().toISOString().slice(0, 16),
  }}
/>
            <Field
              label="Estado"
              value={formData.estado}
              onChange={(v) => setFormData({ ...formData, estado: v })}
              type="select"
              disabled={saving}
              options={[
                { value: ESTADO_EVENTO.PROGRAMADO, label: "📅 Programado" },
                { value: ESTADO_EVENTO.EN_CURSO, label: "▶️ En curso" },
                { value: ESTADO_EVENTO.FINALIZADO, label: "✅ Finalizado" },
                { value: ESTADO_EVENTO.CANCELADO, label: "❌ Cancelado" },
              ]}
              required
            />

            {/* Mostrar selector de grupo solo si es entrenamiento; si es torneo, mostrar selector de entrenador en la misma posición */}
            {formData.tipo === TIPO_EVENTO.ENTRENAMIENTO ? (
              <Field
                label="Grupo asignado"
                value={formData.grupo_id}
                onChange={(v) => setFormData({ ...formData, grupo_id: v })}
                type="select"
                disabled={saving}
                options={[
                  { value: "", label: "Seleccione un grupo..." },
                  ...grupos.map((g) => ({
                    value: g.id,
                    label: g.nombre || `Grupo ${g.id}`,
                  })),
                ]}
                required
              />
            ) : (
              <Field
                label="Entrenador responsable"
                value={formData.colaborador_id}
                onChange={(v) => setFormData({ ...formData, colaborador_id: v })}
                type="select"
                disabled={saving}
                options={[
                  { value: "", label: "Seleccione un entrenador..." },
                  ...entrenadores
                    .filter((e) => e.activo === true || e.activo === undefined)
                    .map((e) => ({ value: e.id, label: `${e.nombres} ${e.apellidos}` })),
                ]}
                required={false}
              />
            )}
          </div>

          <Field
            label="Observaciones"
            value={formData.descripcion}
            onChange={(v) => setFormData({ ...formData, descripcion: v })}
            type="textarea"
            disabled={saving}
            placeholder="Describe los objetivos y actividades del evento..."
          />

          {formData.tipo === TIPO_EVENTO.TORNEO && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 text-amber-800 px-3 py-2 text-xs flex items-center gap-2">
              <span className="text-base">ℹ️</span>
              <span>Los torneos no requieren grupo asignado.</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && (
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {saving
                ? "Guardando..."
                : isEditing
                ? "Guardar cambios"
                : "Crear evento"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
